"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRumViewTimings = exports.RumViewTimingStatus = void 0;
var react_1 = require("react");
var PageLoadProvider_1 = require("./PageLoadProvider");
// whether the RUM vew timing has finished loading or it was canceled due to an error
var RumViewTimingStatus;
(function (RumViewTimingStatus) {
    RumViewTimingStatus["TRACKED"] = "tracked";
    RumViewTimingStatus["CANCELLED"] = "cancelled";
})(RumViewTimingStatus = exports.RumViewTimingStatus || (exports.RumViewTimingStatus = {}));
// helper function
var getErrorAsString = function (error) {
    if (error instanceof Error) {
        return error.message;
    }
    return error ? error : error;
};
/**
 * Hook to keep track RUM view timings for a page load. Useful in pages where there is lots of content loaded and
 * we want to split load times in "sub-metrics" so we keep track of load times for each "piece" separately as well
 * as the total page load duration.
 *
 * @param rumViewTimings The list of rum view timings to keep track of
 * @param pageError A function that returns an error object or string when page load cannot continue.
 * @param data The data to add to tracking.
 */
var useRumViewTimings = function (_a) {
    var rumViewTimings = _a.rumViewTimings, _b = _a.pageError, pageError = _b === void 0 ? function () { return undefined; } : _b, _c = _a.data, data = _c === void 0 ? {} : _c;
    var _d = (0, PageLoadProvider_1.usePageLoadApi)(), url = _d.url, trackPageLoad = _d.trackPageLoad, initMetrics = _d.initMetrics;
    // a ref to set the time when we start measuring.
    var measureStartRef = (0, react_1.useRef)(new Date().getTime());
    // keeps track of the RUM view timings that finished loading; successfully or resulting in error
    var finishedRumViewTimings = (0, react_1.useRef)({});
    // keeps track of the duration the main content finished loading (all view timings with isMainContent = true)
    var mainContentLoadDurationRef = (0, react_1.useRef)(null);
    var allDoneRef = (0, react_1.useRef)(false);
    var pageErrorRef = (0, react_1.useRef)();
    /**
     * Init metrics once page is mounted to guaranteed we always have a metrics client initialized.
     * We also set the timestamp we start measuring at this time.
     */
    (0, react_1.useEffect)(function () {
        measureStartRef.current = new Date().getTime();
        allDoneRef.current = false;
        pageErrorRef.current = undefined;
        mainContentLoadDurationRef.current = null;
        finishedRumViewTimings.current = {};
        initMetrics(url);
    }, [initMetrics, url]);
    /**
     * Callback to record metrics data.
     */
    var track = (0, react_1.useCallback)(function () {
        if (allDoneRef.current)
            return;
        allDoneRef.current = true;
        var pageData = __assign(__assign({}, data), { pageTimings: __assign({ mainContent: mainContentLoadDurationRef.current }, finishedRumViewTimings.current) });
        if (pageErrorRef.current) {
            trackPageLoad(url, 'error', __assign(__assign({}, pageData), { type: 'handled_error', error_message: getErrorAsString(pageErrorRef.current).substr(0, 30) }));
        }
        else {
            trackPageLoad(url, 'success', __assign(__assign({}, pageData), { type: 'tracked' }));
        }
    }, [trackPageLoad, url, data]);
    // in case of a page error we want to stop processing everything and just report the data measured thus far
    var thePageError = pageError();
    (0, react_1.useEffect)(function () {
        if (thePageError && !allDoneRef.current) {
            var timeNow = new Date().getTime();
            if (DD_RUM) {
                DD_RUM.addTiming('pageError');
            }
            for (var i = 0; i < rumViewTimings.length; i++) {
                var current = rumViewTimings[i];
                if (!finishedRumViewTimings.current[current.name]) {
                    finishedRumViewTimings.current[current.name] = {
                        duration: timeNow - measureStartRef.current,
                        isMainLoad: current.isMainLoad,
                        status: RumViewTimingStatus.CANCELLED
                    };
                }
            }
            pageErrorRef.current = thePageError;
            mainContentLoadDurationRef.current = timeNow - measureStartRef.current;
            track();
        }
    }, [thePageError]);
    /**
     * When loading switches from true to false or the error is set, we record the time elapsed in milliseconds and
     * mark it as processed
     */
    (0, react_1.useEffect)(function () {
        var timeNow = new Date().getTime();
        for (var i = 0; i < rumViewTimings.length; i++) {
            var current = rumViewTimings[i];
            var loading = typeof current.loading === 'function' ? current.loading() : current.loading;
            var hasError = Boolean(current.error);
            var currentItemHasBeenProcessed = !!finishedRumViewTimings.current[current.name];
            var error = getErrorAsString(current.error);
            if (!currentItemHasBeenProcessed && (!loading || hasError)) {
                var duration = timeNow - measureStartRef.current;
                finishedRumViewTimings.current[current.name] = {
                    duration: duration,
                    error: error,
                    status: RumViewTimingStatus.TRACKED,
                    isMainLoad: current.isMainLoad
                };
                if (DD_RUM) {
                    DD_RUM.addTiming(current.name);
                }
            }
        }
        // true if at least one metric is not in the finished map
        var moreLeft = rumViewTimings.some(function (metric) { return finishedRumViewTimings.current[metric.name] === undefined; });
        // true if at least one metric with isMainLoad = true is not yet finished
        var mainContentStillLoading = rumViewTimings.some(function (metric) { return metric.isMainLoad && !finishedRumViewTimings.current[metric.name]; });
        // only update duration if not already set
        if (!mainContentLoadDurationRef.current && !mainContentStillLoading) {
            mainContentLoadDurationRef.current = timeNow - measureStartRef.current;
        }
        // are we done processing all sub-metrics
        if (!moreLeft && !mainContentStillLoading) {
            track();
        }
    }, [rumViewTimings]);
};
exports.useRumViewTimings = useRumViewTimings;
